import { type FC } from "react";
import { cn } from "../lib/utils";
import { _BaseButton, type _BaseButtonProps } from "./_BaseButton";
import { Icon, type IconColor, type IconProps } from "./Icon";

/**
 * Resting `color` → the token the icon fades to on hover, applied as a `group-hover:` class so
 * hovering anywhere on the button retones the icon. Mirrors `Button`'s interaction palette.
 */
const hoverColorClasses: Record<IconColor, string> = {
	brand: "group-hover:text-brand-primary-lighter",
	accent: "group-hover:text-button-primary-hover",
	primary: "group-hover:text-button-primary-hover",
	secondary: "group-hover:text-primary",
	tertiary: "group-hover:text-secondary",
	subtle: "group-hover:text-tertiary",
	inverse: "group-hover:text-button-inverse-hover",
	confirm: "group-hover:text-confirm-hover",
	warning: "group-hover:text-intent-warning-text-hover",
	danger: "group-hover:text-danger-hover",
};

export type ClickableIconProps = Omit<_BaseButtonProps, "color" | "type" | "children" | "title"> & {
	/** Which glyph to render. Passed straight through to the inner `Icon`. */
	type: IconProps["type"];

	/**
	 * Size of the icon, passed straight through to the inner `Icon`
	 * (large: 30px, medium: 24px, small: 20px, mini: 16px, micro: 12px)
	 * @default "medium"
	 */
	size?: IconProps["size"];

	/**
	 * Semantic color of the icon at rest. When set, the icon fades to a matching hover tone on
	 * hover. Omit to inherit the surrounding color (no hover retone).
	 */
	color?: IconProps["color"];

	/** Render the solid glyph instead of the outline glyph. Passed through to the inner `Icon`. */
	filled?: boolean;

	/** Tooltip text, rendered as the button's native `title`. */
	tooltip?: string;
};

/**
 * Clickable icon primitive. Renders an `Icon` inside a `_BaseButton`, so the tracking/analytics
 * wiring and native `<button>` semantics (keyboard, focus, `onClick`, `disabled`, `aria-*`) come
 * for free. The icon props (`type`/`size`/`color`/`filled`) pass straight through; `ClickableIcon`
 * adds only the interactive layer — a color fade on hover and a disabled treatment.
 *
 * Sizes: (large: 30px, medium: 24px, small: 20px, mini: 16px, micro: 12px)
 *
 * Reach for `Icon` when you need a static, non-interactive glyph; reach for `Button` with a
 * `leadingIcon`/`trailingIcon` when you need a chrome'd, labeled call-to-action.
 */
export const ClickableIcon: FC<ClickableIconProps> = ({
	type,
	size,
	color,
	filled,
	tooltip,
	className,
	ref,
	...rest
}) => (
	<_BaseButton
		ref={ref}
		title={tooltip}
		className={cn(
			"group inline-flex cursor-pointer items-center justify-center disabled:cursor-not-allowed disabled:opacity-[0.45]",
			className,
		)}
		{...rest}
	>
		<Icon
			type={type}
			size={size}
			color={color}
			filled={filled}
			className={cn("transition-colors", color && hoverColorClasses[color])}
		/>
	</_BaseButton>
);
ClickableIcon.displayName = "ClickableIcon";
